import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  Button,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormDialog } from '@/components/common/FormDialog';
import { useAddLaborItemTime, useDeleteLaborItemTime, type ServiceLaborItemTime } from '@/hooks/useServices';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';

const MANAGE_SERVICES_PERMISSION = 'settings.manage_services';

interface LaborItemVehicleTimesDialogProps {
  open: boolean;
  onClose: () => void;
  serviceId: string;
  laborItemId: string;
  laborItemDescription: string;
  times: ServiceLaborItemTime[];
}

export function LaborItemVehicleTimesDialog({
  open,
  onClose,
  serviceId,
  laborItemId,
  laborItemDescription,
  times,
}: LaborItemVehicleTimesDialogProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Vehicle-time writes require settings.manage_services server-side; hide what would 403.
  const canManageServices = hasPermission(MANAGE_SERVICES_PERMISSION);
  const notify = useNotify();
  const addTime = useAddLaborItemTime();
  const deleteTime = useDeleteLaborItemTime();
  const [rows, setRows] = useState(times);

  useEffect(() => {
    if (open) setRows(times);
  }, [open, times]);

  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [hours, setHours] = useState('1');

  const resetDraft = () => {
    setMake('');
    setModel('');
    setYearFrom('');
    setYearTo('');
    setHours('1');
  };

  const handleAdd = async () => {
    if (!hasPermission(MANAGE_SERVICES_PERMISSION)) return;
    const parsedHours = Number(hours.replace(',', '.'));
    if (!make.trim() || !Number.isFinite(parsedHours) || parsedHours < 0) return;
    const from = yearFrom.trim() === '' ? null : Number(yearFrom);
    const to = yearTo.trim() === '' ? null : Number(yearTo);
    try {
      const created = await addTime.mutateAsync({
        serviceId,
        itemId: laborItemId,
        data: {
          make: make.trim(),
          model: model.trim() || null,
          yearFrom: from != null && Number.isFinite(from) ? from : null,
          yearTo: to != null && Number.isFinite(to) ? to : null,
          estimatedHours: parsedHours,
        },
      });
      setRows((prev) => [...prev, created]);
      resetDraft();
      notify.success(t('servicesTab.vehicleTimeSaved'));
    } catch {
      notify.error(t('servicesTab.vehicleTimeSaveFailed'));
    }
  };

  const handleDelete = async (timeId: string) => {
    if (!hasPermission(MANAGE_SERVICES_PERMISSION)) return;
    try {
      await deleteTime.mutateAsync({ serviceId, itemId: laborItemId, timeId });
      setRows((prev) => prev.filter((row) => row.id !== timeId));
      notify.success(t('servicesTab.vehicleTimeDeleted'));
    } catch {
      notify.error(t('servicesTab.vehicleTimeDeleteFailed'));
    }
  };

  const formatRange = (row: ServiceLaborItemTime) => {
    if (row.yearFrom == null && row.yearTo == null) return t('servicesTab.vehicleTimeAnyYear');
    if (row.yearFrom != null && row.yearTo != null) return `${String(row.yearFrom)}–${String(row.yearTo)}`;
    if (row.yearFrom != null) return `${String(row.yearFrom)}–`;
    return `–${String(row.yearTo)}`;
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('servicesTab.vehicleTimesTitle', { name: laborItemDescription || t('servicesTab.laborItemDesc') })}
      isDirty={false}
      maxWidth="md"
      renderActions={({ onClose: close }) => (
        <Button onClick={close}>{tc('close')}</Button>
      )}
    >
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('servicesTab.vehicleTimesHint')}
        </Typography>

        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('servicesTab.vehicleTimeMake')}</TableCell>
              <TableCell>{t('servicesTab.vehicleTimeModel')}</TableCell>
              <TableCell>{t('servicesTab.vehicleTimeYears')}</TableCell>
              <TableCell align="right">{t('servicesTab.laborItemHours')}</TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    {t('servicesTab.vehicleTimesEmpty')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.make}</TableCell>
                <TableCell>{row.model ?? t('servicesTab.vehicleTimeAnyModel')}</TableCell>
                <TableCell>{formatRange(row)}</TableCell>
                <TableCell align="right">{row.estimatedHours}</TableCell>
                <TableCell>
                  {canManageServices && (
                    <Tooltip title={tc('delete')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => { void handleDelete(row.id); }}
                        aria-label={tc('delete')}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {canManageServices && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-end' }}>
            <TextField
              size="small"
              label={t('servicesTab.vehicleTimeMake')}
              value={make}
              onChange={(e) => setMake(e.target.value)}
            />
            <TextField
              size="small"
              label={t('servicesTab.vehicleTimeModel')}
              value={model}
              onChange={(e) => setModel(e.target.value)}
            />
            <TextField
              size="small"
              label={t('servicesTab.vehicleTimeYearFrom')}
              value={yearFrom}
              onChange={(e) => setYearFrom(e.target.value)}
              sx={{ width: 110 }}
            />
            <TextField
              size="small"
              label={t('servicesTab.vehicleTimeYearTo')}
              value={yearTo}
              onChange={(e) => setYearTo(e.target.value)}
              sx={{ width: 110 }}
            />
            <TextField
              size="small"
              type="number"
              label={t('servicesTab.laborItemHours')}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              slotProps={{ htmlInput: { min: 0, step: 0.25 } }}
              sx={{ width: 120 }}
            />
            <Button
              variant="contained"
              onClick={() => { void handleAdd(); }}
              disabled={!make.trim() || addTime.isPending}
            >
              {t('servicesTab.addVehicleTime')}
            </Button>
          </Stack>
        )}
      </DialogContent>
    </FormDialog>
  );
}
